import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  Subforum,
  Post,
  PostListItem,
  Comment,
  Vote,
  VoteType,
  SortOption,
  PostWithScore,
} from './types';
import { forumApi } from '@/services/endpoints/forum';
import { useAuth } from '@/app/hooks/useAuth';

interface ForumState {
  subforums: Subforum[];
  /**
   * Whether this organization accepts visitor-submitted category suggestions.
   *
   * Arrives on the subforum list read, so it costs no extra request. Starts
   * FALSE and stays false if the API does not send it: the flag decides whether
   * the suggestion entry point renders at all, and guessing "yes" leads a
   * visitor into a form that ends in a 404.
   */
  allowsSubforumRequests: boolean;
  posts: Map<string, PostListItem[]>; // Map<subforumId, list rows>
  /**
   * Open threads, each read from the post DETAIL route (Map<postId, Post>).
   *
   * Kept apart from `posts` on purpose: a list row is losing `content` (the API
   * follow-up to T100526003), so an open thread must never be rendered from
   * one. A list read never writes here and a detail read never writes into
   * `posts`.
   */
  postDetails: Map<string, Post>;
  comments: Map<string, Comment[]>; // Map<postId, Comment[]>
  votes: Vote[];
  anonymousUserId: string;
  sortOption: SortOption;
  loading: {
    subforums: boolean;
    posts: Set<string>; // Set of subforumIds being loaded
    post: Set<string>; // Set of postIds being loaded individually
    comments: Set<string>; // Set of postIds being loaded
    votes: boolean;
  };
  errors: {
    subforums: string | null;
    posts: Map<string, string>; // Map<subforumId, error>
    post: Map<string, string>; // Map<postId, error>
    comments: Map<string, string>; // Map<postId, error>
    votes: string | null;
  };
}

interface ForumContextType {
  state: ForumState;
  // Subforum actions
  getSubforum: (id: string) => Subforum | undefined;
  getSubforumBySlug: (slug: string) => Subforum | undefined;
  fetchSubforums: () => Promise<void>;
  /**
   * SUGGEST a category. Resolves when the suggestion is queued, not when a
   * subforum exists — one may never exist. Rejects with a message fit to show
   * the visitor.
   */
  requestSubforum: (name: string, slug: string, description: string) => Promise<void>;
  // Post actions
  getPostsBySubforum: (subforumId: string) => PostWithScore[];
  /**
   * The post's counts and space: the open thread when it is loaded, else its
   * list row. Never read the body from this; use `getPostDetail`.
   */
  getPost: (id: string) => PostListItem | undefined;
  /** The open thread, as the post detail route returned it, body included. */
  getPostDetail: (id: string) => Post | undefined;
  fetchPosts: (subforumId: string, force?: boolean) => Promise<void>;
  /** Reads the post detail route into `postDetails`; `force` re-reads a loaded one. */
  fetchPost: (postId: string, force?: boolean) => Promise<void>;
  recordPostView: (postId: string) => Promise<void>;
  createPost: (subforumId: string, title: string, content: string) => Promise<void>;
  // Comment actions
  getCommentsByPost: (postId: string) => Comment[];
  fetchComments: (postId: string, force?: boolean) => Promise<void>;
  createComment: (postId: string, content: string, parentId?: string | null) => Promise<void>;
  // Voting actions
  voteOnPost: (postId: string, voteType: VoteType) => Promise<void>;
  voteOnComment: (commentId: string, voteType: VoteType) => Promise<void>;
  getUserVote: (itemId: string, itemType: 'post' | 'comment') => VoteType;
  // Sorting
  setSortOption: (option: SortOption) => void;
}

export const ForumContext = createContext<ForumContextType | undefined>(undefined);

const ANONYMOUS_USER_KEY = 'forum_anonymous_user_id';
// Initialize anonymous user ID from localStorage or generate new one
// This UUID is used to differentiate anonymous users for voting restrictions
const getOrCreateAnonymousUserId = (): string => {
  const stored = localStorage.getItem(ANONYMOUS_USER_KEY);
  if (stored) {
    return stored;
  }
  const newId = crypto.randomUUID();
  localStorage.setItem(ANONYMOUS_USER_KEY, newId);
  return newId;
};

// Calculate hot score (Reddit-style algorithm)
// Formula: score / (age_in_hours + 2)^1.8
const calculateHotScore = (post: PostListItem): number => {
  const score = post.upvotes - post.downvotes;
  const createdAt = new Date(post.createdAt).getTime();
  const now = Date.now();
  const ageInHours = (now - createdAt) / (1000 * 60 * 60);
  const denominator = Math.pow(ageInHours + 2, 1.8);
  return score / denominator;
};

// Sort posts based on selected option
const sortPosts = (posts: PostWithScore[], sortOption: SortOption): PostWithScore[] => {
  const sorted = [...posts];
  
  switch (sortOption) {
    case 'hot':
      return sorted.sort((a, b) => b.hotScore - a.hotScore);
    case 'new':
      return sorted.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    case 'top':
      return sorted.sort((a, b) => b.score - a.score);
    default:
      return sorted;
  }
};

// Build comment tree from flat list
const buildCommentTree = (comments: Comment[]): Comment[] => {
  const commentMap = new Map<string, Comment>();
  const rootComments: Comment[] = [];

  // First pass: create map and find roots
  comments.forEach(comment => {
    const commentWithChildren: Comment = { ...comment, children: [] };
    commentMap.set(comment.id, commentWithChildren);
    if (!comment.parentId) {
      rootComments.push(commentWithChildren);
    }
  });

  // Second pass: build tree
  comments
    .filter(c => c.parentId)
    .forEach(comment => {
      const parent = commentMap.get(comment.parentId!);
      const commentWithChildren: Comment = { ...comment, children: [] };
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(commentWithChildren);
      }
      commentMap.set(comment.id, commentWithChildren);
    });

  // Sort comments by score (descending)
  const sortComments = (comments: Comment[]): Comment[] => {
    return comments
      .sort((a, b) => (b.upvotes - b.downvotes) - (a.upvotes - a.downvotes))
      .map(comment => ({
        ...comment,
        children: comment.children ? sortComments(comment.children) : [],
      }));
  };

  return sortComments(rootComments);
};

/**
 * `prev` with one post's vote counts set in its list row and in the open
 * thread, wherever each is loaded. A list that is not loaded stays unloaded:
 * writing an empty one would make the next `fetchPosts` think it had loaded.
 */
const withPostCounts = (
  prev: ForumState,
  postId: string,
  subforumId: string,
  upvotes: number,
  downvotes: number,
): ForumState => {
  const newPosts = new Map(prev.posts);
  const subforumPosts = newPosts.get(subforumId);
  if (subforumPosts) {
    newPosts.set(
      subforumId,
      subforumPosts.map(p => (p.id === postId ? { ...p, upvotes, downvotes } : p)),
    );
  }
  const detail = prev.postDetails.get(postId);
  if (!detail) return { ...prev, posts: newPosts };
  const newDetails = new Map(prev.postDetails);
  newDetails.set(postId, { ...detail, upvotes, downvotes });
  return { ...prev, posts: newPosts, postDetails: newDetails };
};

export function ForumProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [state, setState] = useState<ForumState>(() => {
    const anonymousUserId = getOrCreateAnonymousUserId();
    
    return {
      subforums: [],
      allowsSubforumRequests: false,
      posts: new Map(),
      postDetails: new Map(),
      comments: new Map(),
      votes: [],
      anonymousUserId,
      sortOption: 'hot',
      loading: {
        subforums: false,
        posts: new Set(),
        post: new Set(),
        comments: new Set(),
        votes: false,
      },
      errors: {
        subforums: null,
        posts: new Map(),
        post: new Map(),
        comments: new Map(),
        votes: null,
      },
    };
  });

  // The result is stored and shown publicly as the post's author, so it never falls back to the
  // email address: a user with no name on file posts under the same neutral label as a visitor.
  const getAuthorName = useCallback((): string => {
    if (!isAuthenticated || !user) {
      return 'Anonymous User';
    }
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || 'Anonymous User';
  }, [isAuthenticated, user]);

  // There is no endpoint to read a viewer's past votes any more: the route it
  // used, `GET /forum/votes/user/:userId`, took a caller-supplied id and handed
  // back that person's voting history, so it was removed server-side (forum spec
  // F3). It was still being called here on every page load, one dead request per
  // visit.
  //
  // `state.votes` is still populated — `createVote` adds to it as the visitor
  // votes — so a vote cast in this session still shows as cast. What is gone is
  // recognising an anonymous visitor's votes after a RELOAD, which spec F6
  // already classes as weak-by-design and requires be labelled rather than
  // relied on. The EMS portal made the same trade for the same reason.
  useEffect(() => {
    fetchSubforums();
  }, []);

  const fetchSubforums = useCallback(async () => {
    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, subforums: true },
      errors: { ...prev.errors, subforums: null },
    }));

    try {
      const response = await forumApi.getSubforums();
      setState(prev => ({
        ...prev,
        subforums: response.data,
        // Under `meta`, and absent at either level reads as false — see the
        // field's note on `ForumState`.
        allowsSubforumRequests: response.meta?.allowsSubforumRequests ?? false,
        loading: { ...prev.loading, subforums: false },
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch subforums';
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, subforums: false },
        errors: { ...prev.errors, subforums: errorMessage },
      }));
    }
  }, []);

  const requestSubforum = useCallback(async (name: string, slug: string, description: string) => {
    await forumApi.requestSubforum({
      name: name.trim(),
      slug: slug.trim(),
      description: description.trim(),
    });
    // DELIBERATELY does not touch `state.subforums`. The old create call pushed
    // the new row in here, which was right when this created a subforum; a
    // suggestion creates nothing, so adding one would put a category on the
    // page that no visitor — including this one, after a reload — can actually
    // open. It appears when an admin approves it and the next read returns it.
    //
    // The error is rethrown untouched rather than logged and swallowed: the
    // form shows the message, and for the full-queue case that message is the
    // only thing that tells the visitor to come back later instead of retrying.
  }, []);

  const fetchPosts = useCallback(async (subforumId: string, force = false) => {
    // Skip if already loading, or already loaded (unless forced)
    if (!force && (state.loading.posts.has(subforumId) || state.posts.has(subforumId))) {
      return;
    }

    setState(prev => ({
      ...prev,
      loading: {
        ...prev.loading,
        posts: new Set(prev.loading.posts).add(subforumId),
      },
      errors: {
        ...prev.errors,
        posts: new Map(prev.errors.posts).set(subforumId, ''),
      },
    }));

    try {
      const response = await forumApi.getPosts({
        subforumId,
        sort: state.sortOption,
      });
      setState(prev => {
        const newPosts = new Map(prev.posts);
        newPosts.set(subforumId, response.data);
        return {
          ...prev,
          posts: newPosts,
          loading: {
            ...prev.loading,
            posts: new Set([...prev.loading.posts].filter(id => id !== subforumId)),
          },
        };
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch posts';
      setState(prev => {
        const newErrors = new Map(prev.errors.posts);
        newErrors.set(subforumId, errorMessage);
        return {
          ...prev,
          loading: {
            ...prev.loading,
            posts: new Set([...prev.loading.posts].filter(id => id !== subforumId)),
          },
          errors: {
            ...prev.errors,
            posts: newErrors,
          },
        };
      });
    }
  }, [state.sortOption, state.loading.posts, state.posts]);

  // Always the DETAIL route, even when a loaded list already holds the post.
  // It used to return early in that case and render the open thread from the
  // list row, and it put the detail into the list map, where the next list
  // read replaced it. List rows are losing `content` (T100526003), so either
  // path would leave an open thread with no body.
  const fetchPost = useCallback(async (postId: string, force = false) => {
    if (state.loading.post.has(postId)) {
      return;
    }
    // A failed read is not retried unforced: the thread page re-runs this
    // effect every time `fetchPost` changes identity, so retrying here turned
    // one 404 into a request loop.
    if (!force && (state.postDetails.has(postId) || state.errors.post.has(postId))) {
      return;
    }

    setState(prev => {
      const newErrors = new Map(prev.errors.post);
      newErrors.delete(postId);
      return {
        ...prev,
        loading: {
          ...prev.loading,
          post: new Set(prev.loading.post).add(postId),
        },
        errors: {
          ...prev.errors,
          post: newErrors,
        },
      };
    });

    try {
      const response = await forumApi.getPostById(postId);
      const post = response.data;
      setState(prev => {
        const newDetails = new Map(prev.postDetails);
        newDetails.set(postId, post);
        return {
          ...prev,
          postDetails: newDetails,
          loading: {
            ...prev.loading,
            post: new Set([...prev.loading.post].filter(id => id !== postId)),
          },
        };
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch post';
      console.error('Failed to fetch post:', error);
      setState(prev => {
        const newErrors = new Map(prev.errors.post);
        newErrors.set(postId, errorMessage);
        return {
          ...prev,
          loading: {
            ...prev.loading,
            post: new Set([...prev.loading.post].filter(id => id !== postId)),
          },
          errors: {
            ...prev.errors,
            post: newErrors,
          },
        };
      });
    }
  }, [state.postDetails, state.loading.post, state.errors.post]);

  const recordPostView = useCallback(async (postId: string) => {
    try {
      await forumApi.recordPostView(postId);
      setState(prev => {
        const newPosts = new Map(prev.posts);
        for (const [subforumId, posts] of newPosts) {
          const idx = posts.findIndex(p => p.id === postId);
          if (idx >= 0) {
            const updated = [...posts];
            updated[idx] = { ...updated[idx], views: (updated[idx].views ?? 0) + 1 };
            newPosts.set(subforumId, updated);
            break;
          }
        }
        const detail = prev.postDetails.get(postId);
        if (!detail) return { ...prev, posts: newPosts };
        const newDetails = new Map(prev.postDetails);
        newDetails.set(postId, { ...detail, views: (detail.views ?? 0) + 1 });
        return { ...prev, posts: newPosts, postDetails: newDetails };
      });
    } catch {
    }
  }, []);

  const fetchComments = useCallback(async (postId: string, force = false) => {
    if (!force && (state.loading.comments.has(postId) || state.comments.has(postId))) {
      return;
    }

    setState(prev => ({
      ...prev,
      loading: {
        ...prev.loading,
        comments: new Set(prev.loading.comments).add(postId),
      },
      errors: {
        ...prev.errors,
        comments: new Map(prev.errors.comments).set(postId, ''),
      },
    }));

    try {
      const response = await forumApi.getComments(postId);
      setState(prev => {
        const newComments = new Map(prev.comments);
        newComments.set(postId, response.data);
        return {
          ...prev,
          comments: newComments,
          loading: {
            ...prev.loading,
            comments: new Set([...prev.loading.comments].filter(id => id !== postId)),
          },
        };
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch comments';
      setState(prev => {
        const newErrors = new Map(prev.errors.comments);
        newErrors.set(postId, errorMessage);
        return {
          ...prev,
          loading: {
            ...prev.loading,
            comments: new Set([...prev.loading.comments].filter(id => id !== postId)),
          },
          errors: {
            ...prev.errors,
            comments: newErrors,
          },
        };
      });
    }
  }, [state.loading.comments, state.comments]);

  const getSubforum = useCallback((id: string): Subforum | undefined => {
    return state.subforums.find(s => s.id === id);
  }, [state.subforums]);

  const getSubforumBySlug = useCallback((slug: string): Subforum | undefined => {
    return state.subforums.find(s => s.slug === slug);
  }, [state.subforums]);

  const getPostDetail = useCallback((id: string): Post | undefined => {
    return state.postDetails.get(id);
  }, [state.postDetails]);

  const getPost = useCallback((id: string): PostListItem | undefined => {
    const detail = state.postDetails.get(id);
    if (detail) return detail;
    for (const posts of state.posts.values()) {
      const post = posts.find(p => p.id === id);
      if (post) return post;
    }
    return undefined;
  }, [state.posts, state.postDetails]);

  const getPostsBySubforum = useCallback((subforumId: string): PostWithScore[] => {
    const posts = state.posts.get(subforumId) || [];
    const postsWithScore: PostWithScore[] = posts.map(post => ({
      ...post,
      score: post.upvotes - post.downvotes,
      hotScore: calculateHotScore(post),
    }));
    return sortPosts(postsWithScore, state.sortOption);
  }, [state.posts, state.sortOption]);

  const getCommentsByPost = useCallback((postId: string): Comment[] => {
    const comments = state.comments.get(postId) || [];
    return buildCommentTree(comments);
  }, [state.comments]);

  const getUserVote = useCallback((itemId: string, itemType: 'post' | 'comment'): VoteType => {
    const vote = state.votes.find(
      v => v.itemId === itemId && 
           v.itemType === itemType && 
           v.userId === state.anonymousUserId
    );
    return vote?.voteType || null;
  }, [state.votes, state.anonymousUserId]);

  const voteOnPost = useCallback(async (postId: string, voteType: VoteType) => {
    if (voteType === null) {
      return;
    }

    const post = getPost(postId);
    if (!post) return;

    const previousVote = state.votes.find(
      v => v.itemId === postId && 
           v.itemType === 'post' && 
           v.userId === state.anonymousUserId
    );

    setState(prev => {
      const existingIndex = prev.votes.findIndex(
        v => v.itemId === postId && 
             v.itemType === 'post' && 
             v.userId === prev.anonymousUserId
      );

      const prevVoteType = previousVote?.voteType || null;
      let newUpvotes = post.upvotes;
      let newDownvotes = post.downvotes;

      if (prevVoteType === 'upvote' && voteType === 'upvote') {
        newUpvotes = Math.max(0, newUpvotes - 1);
      } else if (prevVoteType === 'upvote' && voteType === 'downvote') {
        newUpvotes = Math.max(0, newUpvotes - 1);
        newDownvotes = newDownvotes + 1;
      } else if (prevVoteType === 'downvote' && voteType === 'downvote') {
        newDownvotes = Math.max(0, newDownvotes - 1);
      } else if (prevVoteType === 'downvote' && voteType === 'upvote') {
        newDownvotes = Math.max(0, newDownvotes - 1);
        newUpvotes = newUpvotes + 1;
      } else if (prevVoteType === null && voteType === 'upvote') {
        newUpvotes = newUpvotes + 1;
      } else if (prevVoteType === null && voteType === 'downvote') {
        newDownvotes = newDownvotes + 1;
      }

      return withPostCounts(prev, postId, post.subforumId, newUpvotes, newDownvotes);
    });

    try {
      const response = await forumApi.createVote({
        itemId: postId,
        itemType: 'post',
        voteType: voteType,
        userId: state.anonymousUserId,
      });

      setState(prev => {
        const existingIndex = prev.votes.findIndex(
          v => v.itemId === postId && 
               v.itemType === 'post' && 
               v.userId === prev.anonymousUserId
        );

        let newVotes: Vote[];
        if (response.data.voteType === null) {
          if (existingIndex >= 0) {
            newVotes = prev.votes.filter((_, i) => i !== existingIndex);
          } else {
            newVotes = prev.votes;
          }
        } else if (existingIndex >= 0) {
          newVotes = [...prev.votes];
          newVotes[existingIndex] = response.data;
        } else {
          newVotes = [...prev.votes, response.data];
        }

        return { ...prev, votes: newVotes };
      });

      // Re-read what is on screen for the server's counts: the open thread
      // from its detail route, and the space's list only when one is loaded.
      // The thread page loads no list, and forcing one there only fetched rows
      // it never shows.
      if (state.postDetails.has(postId)) fetchPost(postId, true);
      if (state.posts.has(post.subforumId)) fetchPosts(post.subforumId, true);
    } catch (error) {
      console.error('Failed to vote on post:', error);
      setState(prev =>
        withPostCounts(prev, postId, post.subforumId, post.upvotes, post.downvotes)
      );
    }
  }, [
    state.votes,
    state.anonymousUserId,
    state.postDetails,
    state.posts,
    getPost,
    fetchPost,
    fetchPosts,
  ]);

  const voteOnComment = useCallback(async (commentId: string, voteType: VoteType) => {
    if (voteType === null) {
      return;
    }

    const comment = Array.from(state.comments.values())
      .flat()
      .find(c => c.id === commentId);
    
    if (!comment) return;

    const previousVote = state.votes.find(
      v => v.itemId === commentId && 
           v.itemType === 'comment' && 
           v.userId === state.anonymousUserId
    );

    setState(prev => {
      const prevVoteType = previousVote?.voteType || null;
      let newUpvotes = comment.upvotes;
      let newDownvotes = comment.downvotes;

      if (prevVoteType === 'upvote' && voteType === 'upvote') {
        newUpvotes = Math.max(0, newUpvotes - 1);
      } else if (prevVoteType === 'upvote' && voteType === 'downvote') {
        newUpvotes = Math.max(0, newUpvotes - 1);
        newDownvotes = newDownvotes + 1;
      } else if (prevVoteType === 'downvote' && voteType === 'downvote') {
        newDownvotes = Math.max(0, newDownvotes - 1);
      } else if (prevVoteType === 'downvote' && voteType === 'upvote') {
        newDownvotes = Math.max(0, newDownvotes - 1);
        newUpvotes = newUpvotes + 1;
      } else if (prevVoteType === null && voteType === 'upvote') {
        newUpvotes = newUpvotes + 1;
      } else if (prevVoteType === null && voteType === 'downvote') {
        newDownvotes = newDownvotes + 1;
      }

      const newComments = new Map(prev.comments);
      const postComments = newComments.get(comment.postId) || [];
      const updatedComments = postComments.map(c => 
        c.id === commentId 
          ? { ...c, upvotes: newUpvotes, downvotes: newDownvotes }
          : c
      );
      newComments.set(comment.postId, updatedComments);

      return { ...prev, comments: newComments };
    });

    try {
      const response = await forumApi.createVote({
        itemId: commentId,
        itemType: 'comment',
        voteType: voteType,
        userId: state.anonymousUserId,
      });

      setState(prev => {
        const existingIndex = prev.votes.findIndex(
          v => v.itemId === commentId && 
               v.itemType === 'comment' && 
               v.userId === prev.anonymousUserId
        );

        let newVotes: Vote[];
        if (response.data.voteType === null) {
          if (existingIndex >= 0) {
            newVotes = prev.votes.filter((_, i) => i !== existingIndex);
          } else {
            newVotes = prev.votes;
          }
        } else if (existingIndex >= 0) {
          newVotes = [...prev.votes];
          newVotes[existingIndex] = response.data;
        } else {
          newVotes = [...prev.votes, response.data];
        }

        return { ...prev, votes: newVotes };
      });

      fetchComments(comment.postId, true);
    } catch (error) {
      console.error('Failed to vote on comment:', error);
      setState(prev => {
        const newComments = new Map(prev.comments);
        const postComments = newComments.get(comment.postId) || [];
        const revertedComments = postComments.map(c => 
          c.id === commentId 
            ? { ...c, upvotes: comment.upvotes, downvotes: comment.downvotes }
            : c
        );
        newComments.set(comment.postId, revertedComments);
        return { ...prev, comments: newComments };
      });
    }
  }, [state.votes, state.anonymousUserId, state.comments, fetchComments]);

  const setSortOption = useCallback((option: SortOption) => {
    setState(prev => ({ ...prev, sortOption: option }));
  }, []);

  const createPost = useCallback(async (subforumId: string, title: string, content: string) => {
    try {
      const response = await forumApi.createPost({
        subforumId,
        title: title.trim(),
        content: content.trim(),
        author: getAuthorName(),
      });

      // Add new post to state
      setState(prev => {
        const newPosts = new Map(prev.posts);
        const subforumPosts = newPosts.get(subforumId) || [];
        newPosts.set(subforumId, [response.data, ...subforumPosts]);
        return { ...prev, posts: newPosts };
      });

      // Refresh subforums to update post count
      await fetchSubforums();
    } catch (error) {
      console.error('Failed to create post:', error);
      throw error;
    }
  }, [fetchSubforums, getAuthorName]);

  const createComment = useCallback(async (postId: string, content: string, parentId: string | null = null) => {
    try {
      const response = await forumApi.createComment(postId, {
        content: content.trim(),
        author: getAuthorName(),
        parentId,
      });

      // Add new comment to state
      setState(prev => {
        const newComments = new Map(prev.comments);
        const postComments = newComments.get(postId) || [];
        newComments.set(postId, [...postComments, response.data]);
        return { ...prev, comments: newComments };
      });

      // Re-read the open thread for its new reply count. This used to call
      // `fetchPosts` without forcing it, which skipped the read whenever the
      // list map held the space, so the count never moved.
      if (state.postDetails.has(postId)) {
        await fetchPost(postId, true);
      }
    } catch (error) {
      console.error('Failed to create comment:', error);
      throw error;
    }
  }, [state.postDetails, fetchPost, getAuthorName]);


  const value: ForumContextType = {
    state,
    getSubforum,
    getSubforumBySlug,
    fetchSubforums,
    requestSubforum,
    getPostsBySubforum,
    getPost,
    getPostDetail,
    fetchPosts,
    fetchPost,
    recordPostView,
    createPost,
    getCommentsByPost,
    fetchComments,
    createComment,
    voteOnPost,
    voteOnComment,
    getUserVote,
    setSortOption,
  };

  return <ForumContext.Provider value={value}>{children}</ForumContext.Provider>;
}
